import { Button } from '@heidi/design-system/button';
import { useIntl } from 'react-intl';

import { agentTodoMessages } from '../../../data/projects/timeline/agent-todo/agentTodo.messages';
import { planCardTitle } from '../../../data/projects/timeline/agent-todo/planCardTitle';
import type { TodoState } from '../../../data/projects/timeline/agent-todo/types';
import { PlanCard } from '../timeline/agent-todo/PlanCard';

import type { ApprovalRequestInput } from './ApprovalRequestBlock';
import type { ResolveInlineApproval } from './inline-approval/useInlineApprovalResolve';
import { useInlineApprovalResolve } from './inline-approval/useInlineApprovalResolve';

interface PlanApprovalBlockProps {
  approval: ApprovalRequestInput;
  /** Parsed `create_todo_plan` input — parsed by the caller so a malformed
   * proposal can fall back to the generic approval card instead of a dead end. */
  proposed: TodoState;
  /** Chat title — card header fallback when the proposal has no plan title. */
  titleFallback?: string;
  onResolve: ResolveInlineApproval;
}

/**
 * HITL approval for `create_todo_plan`. Renders the same `PlanCard` the live
 * plan uses — every step pending, ids not yet assigned — with Start plan/Decline
 * inside the card footer, so approving morphs the card in place instead of
 * swapping surfaces.
 */
export const PlanApprovalBlock = ({
  approval,
  proposed,
  titleFallback,
  onResolve,
}: PlanApprovalBlockProps): JSX.Element => {
  const intl = useIntl();
  const { isBusy, submit } = useInlineApprovalResolve({
    approvalId: approval.approval_id,
    onResolve,
  });

  const title = planCardTitle(proposed, intl, titleFallback);

  return (
    <div
      className="flex w-full flex-col"
      data-testid={`plan-approval-${approval.approval_id}`}
    >
      <PlanCard
        title={title}
        items={proposed.items}
        footer={
          <>
            <Button
              variant="ghost"
              size="sm"
              disabled={isBusy}
              onClick={() => submit('deny')}
              data-testid="plan-approval-cancel"
            >
              {intl.formatMessage(agentTodoMessages.declinePlan)}
            </Button>
            <Button
              variant="default"
              size="sm"
              disabled={isBusy}
              onClick={() => submit('approve')}
              data-testid="plan-approval-approve"
            >
              {intl.formatMessage(agentTodoMessages.startPlan)}
            </Button>
          </>
        }
      />
    </div>
  );
};
